import { useEffect } from "react";
import { router, useForm } from "@inertiajs/react";
import { manage_product_path } from "@/routes";

export function nextIndex(items = [], firstIndex = 0) {
  if (items.length === 0) return firstIndex;

  return Math.max(...items.map((item) => Number(item.index) || 0)) + 1;
}

function nestedPath(product, step, collection, record = null) {
  const base = `${manage_product_path(product.id)}/steps/${step.id}/${collection}`;

  return record?.id ? `${base}/${record.id}` : base;
}

function submitOptions(extra = {}) {
  return {
    preserveScroll: true,
    ...extra
  };
}

function selectableTemplates(templates, selectedTemplateId = null) {
  return templates.filter((template) => (
    !template.associated_step_id || template.id.toString() === selectedTemplateId?.toString()
  ));
}

function orderedRecordAttributes(record = {}, includeDescription = false) {
  return {
    name: record.name || "",
    index: record.index ?? 0,
    ...(includeDescription ? { description: record.description || "" } : {})
  };
}

export function StepFieldsForm({ step, updatePath, onProcessingChange = null }) {
  const { data, setData, patch, processing } = useForm({
    step: {
      name: step.name || "",
      description: step.description || "",
      index: step.index ?? 0,
      completion_criteria: step.completion_criteria || ""
    }
  });

  useEffect(() => {
    setData({
      step: {
        name: step.name || "",
        description: step.description || "",
        index: step.index ?? 0,
        completion_criteria: step.completion_criteria || ""
      }
    });
  }, [setData, step]);

  useEffect(() => {
    onProcessingChange?.(processing);
  }, [onProcessingChange, processing]);

  const submit = (event) => {
    event.preventDefault();
    patch(updatePath, submitOptions());
  };

  return (
    <form className="workspace-editor-panel vstack gap-3" onSubmit={submit}>
      <div className="row g-3 workspace-step-fields-grid">
        <div className="col-lg-8">
          <label className="form-label fw-semibold" htmlFor="manage-step-name">Navn</label>
          <input
            id="manage-step-name"
            className="form-control"
            value={data.step.name}
            onChange={(event) => setData("step", { ...data.step, name: event.target.value })}
            required
          />
        </div>

        <div className="col-lg-4">
          <label className="form-label fw-semibold" htmlFor="manage-step-index">Rækkefølge</label>
          <input
            id="manage-step-index"
            className="form-control"
            type="number"
            min="0"
            value={data.step.index}
            onChange={(event) => setData("step", { ...data.step, index: event.target.value })}
            required
          />
        </div>

        <div className="col-12">
          <label className="form-label fw-semibold" htmlFor="manage-step-description">Beskrivelse</label>
          <textarea
            id="manage-step-description"
            className="form-control"
            rows="3"
            value={data.step.description}
            onChange={(event) => setData("step", { ...data.step, description: event.target.value })}
          />
        </div>

        <div className="col-12">
          <label className="form-label fw-semibold" htmlFor="manage-step-completion">Kriterier for at trinnet er løst</label>
          <textarea
            id="manage-step-completion"
            className="form-control"
            rows="3"
            value={data.step.completion_criteria}
            onChange={(event) => setData("step", { ...data.step, completion_criteria: event.target.value })}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <button className="btn btn-primary" disabled={processing}>Gem trin</button>
      </div>
    </form>
  );
}

export function OrderedRecordForm({
  product,
  step,
  record,
  collection,
  paramKey,
  label,
  onDeleteLabel,
  descriptionLabel = null,
  minIndex = 0
}) {
  const includeDescription = Boolean(descriptionLabel);
  const descriptionId = `${paramKey}-${step.id}-${record.id}-description`;
  const { data, setData, patch, processing } = useForm({
    [paramKey]: orderedRecordAttributes(record, includeDescription)
  });

  useEffect(() => {
    setData({
      [paramKey]: orderedRecordAttributes(record, includeDescription)
    });
  }, [includeDescription, paramKey, record, setData]);

  const submit = (event) => {
    event.preventDefault();
    patch(nestedPath(product, step, collection, record), submitOptions());
  };

  const destroy = () => {
    router.delete(nestedPath(product, step, collection, record), submitOptions());
  };

  return (
    <form
      className={`workspace-inline-list__row${includeDescription ? " workspace-inline-list__row--with-description" : ""}`}
      onSubmit={submit}
    >
      <div className="workspace-inline-list__index">
        <label className="form-label fw-semibold">Nr.</label>
        <input
          className="form-control"
          type="number"
          min={minIndex}
          value={data[paramKey].index}
          onChange={(event) => setData(paramKey, { ...data[paramKey], index: event.target.value })}
          required
        />
      </div>

      <div className="workspace-inline-list__name">
        <label className="form-label fw-semibold">{label}</label>
        <input
          className="form-control"
          value={data[paramKey].name}
          onChange={(event) => setData(paramKey, { ...data[paramKey], name: event.target.value })}
          required
        />

        {includeDescription ? (
          <div className="workspace-inline-list__description">
            <label className="form-label fw-semibold" htmlFor={descriptionId}>{descriptionLabel}</label>
            <textarea
              id={descriptionId}
              className="form-control"
              rows="3"
              value={data[paramKey].description}
              onChange={(event) => setData(paramKey, { ...data[paramKey], description: event.target.value })}
            />
          </div>
        ) : null}
      </div>

      <div className="workspace-inline-list__action d-flex gap-2">
        <button className="btn btn-outline-primary" disabled={processing}>Gem</button>
        <button type="button" className="btn btn-outline-danger" onClick={destroy}>
          {onDeleteLabel}
        </button>
      </div>
    </form>
  );
}

export function NewOrderedRecordForm({
  product,
  step,
  collection,
  paramKey,
  label,
  submitLabel,
  defaultIndex,
  descriptionLabel = null,
  minIndex = 0
}) {
  const includeDescription = Boolean(descriptionLabel);
  const descriptionId = `${paramKey}-${step.id}-new-description`;
  const { data, setData, post, processing } = useForm({
    [paramKey]: orderedRecordAttributes({ index: defaultIndex }, includeDescription)
  });

  useEffect(() => {
    setData(paramKey, orderedRecordAttributes({ index: defaultIndex }, includeDescription));
  }, [defaultIndex, includeDescription, paramKey, setData]);

  const submit = (event) => {
    event.preventDefault();
    post(nestedPath(product, step, collection), submitOptions({
      onSuccess: () => {
        const currentIndex = Number(data[paramKey].index);
        const nextDefaultIndex = Number(defaultIndex);
        const nextItemIndex = Number.isNaN(currentIndex) ? nextDefaultIndex + 1 : currentIndex + 1;

        setData(paramKey, orderedRecordAttributes({ index: nextItemIndex }, includeDescription));
      }
    }));
  };

  return (
    <form
      className={`workspace-inline-list__row${includeDescription ? " workspace-inline-list__row--with-description" : ""}`}
      onSubmit={submit}
    >
      <div className="workspace-inline-list__index">
        <label className="form-label fw-semibold">Nr.</label>
        <input
          className="form-control"
          type="number"
          min={minIndex}
          value={data[paramKey].index}
          onChange={(event) => setData(paramKey, { ...data[paramKey], index: event.target.value })}
          required
        />
      </div>

      <div className="workspace-inline-list__name">
        <label className="form-label fw-semibold">{label}</label>
        <input
          className="form-control"
          value={data[paramKey].name}
          onChange={(event) => setData(paramKey, { ...data[paramKey], name: event.target.value })}
          required
        />

        {includeDescription ? (
          <div className="workspace-inline-list__description">
            <label className="form-label fw-semibold" htmlFor={descriptionId}>{descriptionLabel}</label>
            <textarea
              id={descriptionId}
              className="form-control"
              rows="3"
              value={data[paramKey].description}
              onChange={(event) => setData(paramKey, { ...data[paramKey], description: event.target.value })}
            />
          </div>
        ) : null}
      </div>

      <div className="workspace-inline-list__action">
        <button className="btn btn-primary" disabled={processing}>{submitLabel}</button>
      </div>
    </form>
  );
}

export function OrderedSection({ title, eyebrow, emptyLabel, existingCount, children }) {
  return (
    <section className="workspace-editor-panel">
      <div className="mb-3">
        <div className="workspace-section__eyebrow">{eyebrow}</div>
        <h3 className="workspace-editor-panel__title">{title}</h3>
      </div>

      <div className="workspace-inline-list">
        {children}
      </div>

      {existingCount === 0 ? (
        <div className="workspace-editor-panel__empty mt-3">{emptyLabel}</div>
      ) : null}
    </section>
  );
}

export function StepTemplateForm({ product, step, stepTemplate, templates = [] }) {
  const availableTemplates = selectableTemplates(templates, stepTemplate.template_id);
  const { data, setData, patch, processing } = useForm({
    step_template: {
      index: stepTemplate.index ?? 0,
      template_id: stepTemplate.template_id || ""
    }
  });

  useEffect(() => {
    setData({
      step_template: {
        index: stepTemplate.index ?? 0,
        template_id: stepTemplate.template_id || ""
      }
    });
  }, [setData, stepTemplate]);

  const updateStepTemplate = (updates) => {
    setData("step_template", { ...data.step_template, ...updates });
  };

  const submit = (event) => {
    event.preventDefault();
    patch(nestedPath(product, step, "step_templates", stepTemplate), submitOptions());
  };

  const destroy = () => {
    router.delete(nestedPath(product, step, "step_templates", stepTemplate), submitOptions());
  };

  return (
    <form className="workspace-editor-panel vstack gap-3" onSubmit={submit}>
      <div className="d-flex align-items-start justify-content-between gap-3">
        <div>
          <div className="workspace-section__eyebrow">Skabelon</div>
          <h3 className="workspace-editor-panel__title">{stepTemplate.template?.name || "Skabelon"}</h3>
        </div>

        <button type="button" className="btn btn-outline-danger" onClick={destroy}>
          Fjern
        </button>
      </div>

      <div className="row g-3">
        <div className="col-lg-3">
          <label className="form-label fw-semibold">Nr.</label>
          <input
            className="form-control"
            type="number"
            min="0"
            value={data.step_template.index}
            onChange={(event) => updateStepTemplate({ index: event.target.value })}
            required
          />
        </div>

        <div className="col-lg-9">
          <label className="form-label fw-semibold">Skabelon</label>
          <select
            className="form-select"
            value={data.step_template.template_id}
            onChange={(event) => updateStepTemplate({ template_id: event.target.value })}
            required
          >
            <option value="">Vælg en skabelon</option>
            {availableTemplates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}
          </select>
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <button className="btn btn-primary" disabled={processing}>Gem skabelon</button>
      </div>
    </form>
  );
}

export function NewStepTemplateForm({ product, step, defaultIndex, templates = [], onSuccess = null }) {
  const availableTemplates = selectableTemplates(templates);
  const { data, setData, post, processing, reset } = useForm({
    step_template: {
      index: defaultIndex,
      template_id: ""
    }
  });

  useEffect(() => {
    setData("step_template", { index: defaultIndex, template_id: "" });
  }, [defaultIndex, setData]);

  const updateStepTemplate = (updates) => {
    setData("step_template", { ...data.step_template, ...updates });
  };

  const submit = (event) => {
    event.preventDefault();
    post(nestedPath(product, step, "step_templates"), submitOptions({
      onSuccess: () => {
        reset();
        onSuccess?.();
      }
    }));
  };

  return (
    <form className="workspace-editor-panel vstack gap-3" onSubmit={submit}>
      <div>
        <div className="workspace-section__eyebrow">Ny skabelon</div>
        <h3 className="workspace-editor-panel__title">Tilknyt skabelon</h3>
      </div>

      <div className="row g-3">
        <div className="col-lg-3">
          <label className="form-label fw-semibold">Nr.</label>
          <input
            className="form-control"
            type="number"
            min="0"
            value={data.step_template.index}
            onChange={(event) => updateStepTemplate({ index: event.target.value })}
            required
          />
        </div>

        <div className="col-lg-9">
          <label className="form-label fw-semibold">Skabelon</label>
          <select
            className="form-select"
            value={data.step_template.template_id}
            onChange={(event) => updateStepTemplate({ template_id: event.target.value })}
            required
          >
            <option value="">{availableTemplates.length > 0 ? "Vælg en skabelon" : "Ingen ledige skabeloner"}</option>
            {availableTemplates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}
          </select>
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <button className="btn btn-primary" disabled={processing || availableTemplates.length === 0}>Tilknyt skabelon</button>
      </div>
    </form>
  );
}

export function StepFileForm({ product, step, stepTemplate }) {
  const { data, setData, patch, processing } = useForm({
    step_template: {
      index: stepTemplate.index ?? 0,
      name: stepTemplate.name || "",
      file: null
    }
  });

  useEffect(() => {
    setData({
      step_template: {
        index: stepTemplate.index ?? 0,
        name: stepTemplate.name || "",
        file: null
      }
    });
  }, [setData, stepTemplate]);

  const updateStepTemplate = (updates) => {
    setData("step_template", { ...data.step_template, ...updates });
  };

  const submit = (event) => {
    event.preventDefault();
    patch(nestedPath(product, step, "step_templates", stepTemplate), submitOptions({ forceFormData: true }));
  };

  const destroy = () => {
    router.delete(nestedPath(product, step, "step_templates", stepTemplate), submitOptions());
  };

  return (
    <form className="workspace-editor-panel vstack gap-3" onSubmit={submit}>
      <div className="d-flex align-items-start justify-content-between gap-3">
        <div>
          <div className="workspace-section__eyebrow">Fil</div>
          <h3 className="workspace-editor-panel__title">{stepTemplate.name || "Fil uden navn"}</h3>
        </div>

        <button type="button" className="btn btn-outline-danger" onClick={destroy}>
          Fjern
        </button>
      </div>

      <div className="row g-3">
        <div className="col-lg-3">
          <label className="form-label fw-semibold">Nr.</label>
          <input
            className="form-control"
            type="number"
            min="0"
            value={data.step_template.index}
            onChange={(event) => updateStepTemplate({ index: event.target.value })}
            required
          />
        </div>

        <div className="col-lg-9">
          <label className="form-label fw-semibold">Navn</label>
          <input
            className="form-control"
            value={data.step_template.name}
            onChange={(event) => updateStepTemplate({ name: event.target.value })}
            required
          />
        </div>

        <div className="col-12">
          <label className="form-label fw-semibold">Erstat fil</label>
          <input
            className="form-control"
            type="file"
            onChange={(event) => updateStepTemplate({ file: event.target.files?.[0] || null })}
          />
          <div className="form-text">Nuværende fil: {stepTemplate.existing_file_name}</div>
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <button className="btn btn-primary" disabled={processing}>Gem fil</button>
      </div>
    </form>
  );
}

export function NewStepFileForm({ product, step, defaultIndex, onSuccess = null }) {
  const { data, setData, post, processing, reset } = useForm({
    step_template: {
      index: defaultIndex,
      name: "",
      file: null
    }
  });

  useEffect(() => {
    setData("step_template", { index: defaultIndex, name: "", file: null });
  }, [defaultIndex, setData]);

  const updateStepTemplate = (updates) => {
    setData("step_template", { ...data.step_template, ...updates });
  };

  const submit = (event) => {
    event.preventDefault();
    post(nestedPath(product, step, "step_templates"), submitOptions({
      forceFormData: true,
      onSuccess: () => {
        reset();
        onSuccess?.();
      }
    }));
  };

  return (
    <form className="workspace-editor-panel vstack gap-3" onSubmit={submit}>
      <div>
        <div className="workspace-section__eyebrow">Ny fil</div>
        <h3 className="workspace-editor-panel__title">Upload fil</h3>
      </div>

      <div className="row g-3">
        <div className="col-lg-3">
          <label className="form-label fw-semibold">Nr.</label>
          <input
            className="form-control"
            type="number"
            min="0"
            value={data.step_template.index}
            onChange={(event) => updateStepTemplate({ index: event.target.value })}
            required
          />
        </div>

        <div className="col-lg-9">
          <label className="form-label fw-semibold">Navn</label>
          <input
            className="form-control"
            value={data.step_template.name}
            onChange={(event) => updateStepTemplate({ name: event.target.value })}
            placeholder="Bruger filnavnet, hvis feltet er tomt"
          />
        </div>

        <div className="col-12">
          <label className="form-label fw-semibold">Fil</label>
          <input
            className="form-control"
            type="file"
            onChange={(event) => updateStepTemplate({ file: event.target.files?.[0] || null })}
            required
          />
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <button className="btn btn-primary" disabled={processing || !data.step_template.file}>Upload fil</button>
      </div>
    </form>
  );
}

export default function ManageStepEditDrawer({ product, step, templates = [], errors = [], updatePath, onProcessingChange = null }) {
  const subSteps = step.sub_steps_attributes || [];
  const participants = step.step_participants_attributes || [];
  const stepTemplates = step.step_templates_attributes || [];

  return (
    <div className="workspace-content workspace-content--drawer">
      <div className="vstack gap-4 workspace-drawer-form-card card card-body">
        {errors.length > 0 ? (
          <div className="alert alert-danger mb-0">{errors.join(", ")}</div>
        ) : null}

        <StepFieldsForm
          step={step}
          updatePath={updatePath}
          onProcessingChange={onProcessingChange}
        />

        <OrderedSection eyebrow="Undertrin" title="Undertrin" emptyLabel="Ingen undertrin endnu." existingCount={subSteps.length}>
          {subSteps.map((subStep) => (
            <OrderedRecordForm
              key={subStep.id}
              product={product}
              step={step}
              record={subStep}
              collection="sub_steps"
              paramKey="sub_step"
              label="Titel"
              onDeleteLabel="Slet"
              descriptionLabel="Beskrivelse"
              minIndex={1}
            />
          ))}
          <NewOrderedRecordForm
            product={product}
            step={step}
            collection="sub_steps"
            paramKey="sub_step"
            label="Titel"
            submitLabel="Opret"
            defaultIndex={nextIndex(subSteps, 1)}
            descriptionLabel="Beskrivelse"
            minIndex={1}
          />
        </OrderedSection>

        <OrderedSection eyebrow="Roller" title="Roller og ansvarlige" emptyLabel="Ingen roller endnu." existingCount={participants.length}>
          {participants.map((participant) => (
            <OrderedRecordForm
              key={participant.id}
              product={product}
              step={step}
              record={participant}
              collection="step_participants"
              paramKey="step_participant"
              label="Rolle"
              onDeleteLabel="Slet"
            />
          ))}
          <NewOrderedRecordForm
            product={product}
            step={step}
            collection="step_participants"
            paramKey="step_participant"
            label="Ny rolle"
            submitLabel="Opret"
            defaultIndex={nextIndex(participants)}
          />
        </OrderedSection>

        <div className="vstack gap-3">
          {stepTemplates.map((stepTemplate) => (
            stepTemplate.content_kind === "file" ? (
              <StepFileForm
                key={stepTemplate.id}
                product={product}
                step={step}
                stepTemplate={stepTemplate}
              />
            ) : (
              <StepTemplateForm
                key={stepTemplate.id}
                product={product}
                step={step}
                stepTemplate={stepTemplate}
                templates={templates}
              />
            )
          ))}

          <NewStepTemplateForm
            product={product}
            step={step}
            defaultIndex={nextIndex(stepTemplates)}
            templates={templates}
          />

          <NewStepFileForm
            product={product}
            step={step}
            defaultIndex={nextIndex(stepTemplates)}
          />
        </div>
      </div>
    </div>
  );
}
